/**
 * @parkify/brand-tokens — canonical brand tokens (TS).
 *
 * Brand authority: docs/PARKIFY-BRAND-RULES.md. Update that file first;
 * this package implements its decisions for existing UI surfaces.
 *
 * Policy (also enforced in tokens.css):
 *   - Master Purple #7F3F98 is the brand accent; the website must not use it
 *     as full-page wallpaper. Context rules live in the canonical guide.
 *   - Master Black #231F20 is body text. NEVER pure #000.
 *   - Website/marketing uses Mulish; signage/wayfinding/merchandise uses
 *     licensed Avenir. Product font values stay frozen as shipped state.
 *   - Remaining Stack colors are legacy. Preserve existing functional
 *     status/data uses; do not use them for new brand creative.
 *   - Green is permitted only as `success` (#15803D), status indicators
 *     only. There is ONE green. Do not introduce others.
 */

export const colors = {
  // ── Master (primary) palette ────────────────────────────────────────────
  brand:        '#7F3F98',  // Master Purple — hero bg, headers, CTAs
  brandDark:    '#5C2E70',
  brandLight:   '#F0EAF4',
  brandBorder:  '#ECE9EF',

  black:        '#231F20',  // Master Black — body text, footer bg
  white:        '#FFFFFF',

  // Warm Master grays (NOT Tailwind slate).
  // gray600 is TEXT and its value is a contrast floor — see the long note in
  // tokens.css. Keep the two files identical; this is the JS mirror.
  gray100:      '#F8F7F9',
  gray200:      '#F0EFF2',
  gray300:      '#ECE9EF',
  gray400:      '#C9C6CE',
  gray500:      '#B9B9B9',  // Master Gray — decorative/non-text only
  gray600:      '#6B6B6B',  // 5.33:1 on white (was #8A8694, 3.55:1 — under AA)
  gray700:      '#5A5567',

  /**
   * The ground behind a sign-in gate. One value for every product's gate, so
   * "one gate design, one label per product" is structural rather than four
   * copies of a gradient that drift.
   *
   * WAS a full-bleed saturated purple gradient, and it was the only one in the
   * product — every other gate-like moment (a closed-zone screen, a
   * payment-failed screen, the kiosk wall) is a card on a light ground, so the
   * gate read as a different product at the one moment a user is deciding
   * whether they are in the right place. Austin retired it in the demo
   * (parkify-demo 3848038, 2026-08-26); this is the live half.
   *
   * ⚠️ Deliberately an ALIAS of gray100, not the demo's literal #F6F5F8. The two
   * differ by 2/255 per channel — invisible on screen — and this file's own
   * header forbids adding near-duplicate values to the palette. Do not "correct"
   * this back to #F6F5F8: the divergence is recorded on row 31 of
   * docs/DEMO-PARITY-TRACKER.md and is intentional.
   */
  gateField:    '#F8F7F9',  // === gray100

  // ── Legacy Stack palette — existing functional uses only ───────────────
  stackOrange:  '#F16521',
  stackRed:     '#ED1C24',
  stackBlue:    '#213F99',
  stackBrown:   '#A58578',

  // ── Status — single sources of truth ────────────────────────────────────
  // The ONE permitted green. Locked at #15803D.
  success:      '#15803D',
  successBg:    'rgba(21, 128, 61, 0.08)',
  warning:      '#F16521',
  warningBg:    'rgba(241, 101, 33, 0.08)',
  error:        '#ED1C24',
  errorBg:      'rgba(237, 28, 36, 0.08)',
  info:         '#213F99',

  // ── Aliases for migration ergonomics ────────────────────────────────────
  // sales/portal/theme.ts uses `purple` as the key; preserved for parity.
  purple:       '#7F3F98',
} as const

// Frozen shipped typography — NOT the brand answer for either domain.
// Brand rules §3 was updated 2026-09-26: Mulish for website/marketing, Avenir
// for signage/wayfinding/merchandise, Inter as an unvalidated product target.
// This value
// is neither. It is the font shipped surfaces are already on, deliberately
// left alone: Austin froze production and demo fonts in the same decision, so
// "reconciling" this to Mulish or Inter is a migration, not a cleanup, and it
// needs screen validation and a separate sign-off.
// See docs/PARKIFY-BRAND-RULES.md §3.
export const fontFamily =
  "'Montserrat', 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

export const radii = {
  sm:   4,
  md:   12,
  lg:   24,
  xl:   40,
  full: 999,
  /** Action-button radius (primary/secondary/destructive/toolbar actions).
   *  A component decision, not a size step — see the --radius-btn comment in
   *  tokens.css for what it deliberately does NOT cover. */
  btn:  14,
  /** The same decision for controls under ~26px tall (chips, compact segment
   *  items, dense toolbar actions). CSS clamps radius to half the height, so
   *  `btn` on a short control silently renders as a pill — use this instead.
   *  Choose by rendered height, not by component name. */
  chip: 8,
  /** The sign-in gate card (patrol, ops). Mirrors `--radius-gate` in
   *  tokens.css: the demo's ParkifyGate radius, one value for every gate. */
  gate: 26,
} as const

export const shadows = {
  sm:    '0 1px 4px rgba(35,31,32,0.06)',
  md:    '0 4px 16px rgba(35,31,32,0.10)',
  lg:    '0 12px 32px rgba(35,31,32,0.14)',
  brand: '0 4px 14px rgba(127,63,152,0.35)',
} as const

/**
 * Material values mirrored by the `--glass-*` custom properties in tokens.css.
 * These are independent of the gray scale so hosts can override their neutral
 * palette without changing the shared glass language.
 */
export const glass = {
  chromeBg:           'rgba(35, 31, 32, 0.94)',
  chromeBgEdge:       'rgba(35, 31, 32, 0.82)',
  chromeBgCollapsed:  'rgba(35, 31, 32, 0.96)',
  chromeBgSolid:      '#231F20',
  chromeBgMobile:     'rgba(35, 31, 32, 0.86)',
  chromeBorder:       'rgba(255, 255, 255, 0.42)',
  chromeBlur:         '18px',
  chromeBlurMobile:   '20px',
  chromeSaturate:     '165%',
  chromeHighlight:    'rgba(255, 255, 255, 0.32)',
  chromeShadow:       '10px 0 28px rgba(35, 31, 32, 0.22)',
  chromePeek:         '14px',
  elevatedBg:         'rgba(255, 255, 255, 0.78)',
  elevatedBlur:       '24px',
  elevatedShadow:     '0 12px 32px rgba(35, 31, 32, 0.18)',
  scrim:              'rgba(35, 31, 32, 0.58)',
} as const

export const type = {
  hero:         { size: 'clamp(40px, 6vw, 80px)',  weight: 700, tracking: '-2px' },
  section:      { size: 'clamp(32px, 5vw, 60px)',  weight: 700, tracking: '-1.5px' },
  body:         { size: '14px', weight: 400 },
  eyebrow:      { size: '10px', weight: 700, tracking: '3px' },
  sectionLabel: { size: '10px', weight: 700, tracking: '2.5px' },
  navLink:      { size: '11px', weight: 700, tracking: '1.2px' },
  /** The product label under the logo on a sign-in gate. Mirrors
   *  `--tracking-gate-label` in tokens.css. */
  gateLabel:    { size: '12px', weight: 800, tracking: '.34em' },
} as const
